import { TextField } from '@mui/material';
import type { FilledTextFieldProps, Theme, SxProps } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme as SystemTheme } from '@mui/system';
import { forwardRef } from 'react';

type ExactTheme = Theme extends SystemTheme ? Theme : never;

interface Props extends Omit<FilledTextFieldProps, 'variant'> {
  sx?: SxProps<ExactTheme>; // Use ExactTheme instead of Theme
  min?: number;
  max?: number;
  labelSize?: 'small' | 'regular';
}

export const InputField = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const { sx, max, min, labelSize = 'regular', ...rest } = props;

  return (
    <TextField
      {...rest}
      ref={ref}
      variant="filled"
      slotProps={{
        htmlInput: {
          ...props?.slotProps?.htmlInput,
          min,
          max,
          sx: {
            fontSize: 14,
            alignItems: 'center',
            display: 'flex',
            height: 'unset',
            lineHeight: 1.6,
            px: 1.5,
            py: 0.75,
            '&.MuiInputBase-inputAdornedStart': {
              pl: 0,
            },
            '&.Mui-disabled': {
              cursor: 'not-allowed',
            },
            // @ts-expect-error Typescript throwing error when trying to pass sx props to MuiFilledInput-root but styles works properly
            ...props?.slotProps?.htmlInput?.sx,
          },
        },
        inputLabel: {
          shrink: true,
          sx: {
            color: 'text.primary',
            fontSize: labelSize === 'regular' ? 14 : 12,
            fontWeight: 500,
            mb: 0.5,
            position: 'relative',
            transform: 'none',
            whiteSpace: 'pre-wrap',
          },
        },
        formHelperText: {
          sx: {
            ml: 0,
          },
        },
        input: {
          ...props.slotProps?.input,
          disableUnderline: true,
        },
      }}
      sx={{
        '& .MuiFilledInput-root': {
          backgroundColor: 'background.paper',
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: 'neutral.300',
          borderRadius: 1,
          boxShadow: '0px 1px 2px 0px rgba(9, 30, 66, 0.08)',
          overflow: 'hidden',
          p: 0,
          transition: theme => theme.transitions.create(['border-color', 'box-shadow']),
          '&:before': {
            borderBottom: 0,
          },
          '&:hover': {
            backgroundColor: 'background.paper',
          },
          '&.Mui-focused': {
            backgroundColor: 'background.paper',
            boxShadow: theme => `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
          },
          '&.Mui-disabled': {
            backgroundColor: 'action.disabledBackground',
            boxShadow: 'none',
            borderColor: alpha('#D6DBE1', 0.5),
          },
          '.MuiInputAdornment-root.MuiInputAdornment-positionStart.MuiInputAdornment-root:not(.MuiInputAdornment-hiddenLabel)':
            {
              mt: 0,
              ml: 1.5,
            },
        },
        ...sx,
      }}
    />
  );
});

InputField.displayName = 'InputField';
